import type { OddElementType } from '@backoffice-frontend/graphql'
import { InfoIIcon, XIcon } from '@moia-dev/pace-icons'
import {
  Button,
  Heading,
  Icon,
  IconButton,
  Switch,
  Text,
} from '@moia-dev/pace-web'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ODD_TOGGLE_GROUPS } from '../layer-config'
import { useOverwatchMapOddElementsByAttributesLazyQuery } from '../MapOverlays.hook'
import { getOddIconSrc, ODD_PNG_MAP } from '../odd-icons'

const InfoTooltip = ({ text }: { text: string }) => {
  const [visible, setVisible] = useState(false)
  const [style, setStyle] = useState<React.CSSProperties>({})
  const ref = useRef<HTMLDivElement>(null)

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect()
      setStyle({
        position: 'fixed',
        bottom: window.innerHeight - rect.top + 6,
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%)',
        zIndex: 99999,
      })
    }
    setVisible(true)
  }

  return (
    <div
      ref={ref}
      css={{ lineHeight: 0, display: 'inline-flex', cursor: 'help' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setVisible(false)}
    >
      <div css={theme => ({ color: theme.semantic.ColorContentReduced })}>
        <Icon size={16}>
          <InfoIIcon />
        </Icon>
      </div>
      {createPortal(
        <div
          css={theme => ({
            backgroundColor: theme.semantic.ColorSurfaceContrastDefault,
            color: theme.semantic.ColorContentOnContrastCriticalSuccessDefault,
            fontSize: 11,
            lineHeight: 1.4,
            padding: `${theme.space.Space2} ${theme.space.Space3}`,
            borderRadius: 4,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            opacity: visible ? 1 : 0,
            transition: 'opacity 150ms ease',
          })}
          style={style}
        >
          {text}
        </div>,
        document.body,
      )}
    </div>
  )
}

export type OddFilterConfig = {
  containsEnabled: boolean
  contains: OddElementType[]
  excludesEnabled: boolean
  excludes: OddElementType[]
}

export const DEFAULT_FILTER_CONFIG: OddFilterConfig = {
  containsEnabled: false,
  contains: [],
  excludesEnabled: false,
  excludes: [],
}

type OddFilterModalProps = {
  type: string
  label: string
  metropolisId: number
  currentConfig: OddFilterConfig
  onSave: (config: OddFilterConfig, elements: FilteredElements | null) => void
  onClose: () => void
}

export type FilteredElements = {
  taxonomy: string
  elementType: string | null | undefined
  location: { latitude: number; longitude: number } | null | undefined
}[]

type SelectOption = { type: string; label: string; enumType: OddElementType }

const ODD_SELECT_OPTIONS: SelectOption[] = ODD_TOGGLE_GROUPS.flatMap(group =>
  group.items
    .filter(({ type }) => type in ODD_PNG_MAP)
    .map(({ type, label }) => ({
      type,
      label,
      enumType: `ODD_ELEMENT_TYPE_${type}` as OddElementType,
    })),
)

const getEnumType = (type: string): OddElementType =>
  `ODD_ELEMENT_TYPE_${type}` as OddElementType

type MultiSelectProps = {
  value: OddElementType[]
  onChange: (v: OddElementType[]) => void
  disabled: boolean
  excludeTypes: OddElementType[]
}

const MultiSelect = ({
  value,
  onChange,
  disabled,
  excludeTypes,
}: MultiSelectProps) => {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width,
      })
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleMouseDown = (e: MouseEvent) => {
      if (
        buttonRef.current?.contains(e.target as Node) ||
        dropdownRef.current?.contains(e.target as Node)
      )
        return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open])

  const toggle = (enumType: OddElementType) => {
    onChange(
      value.includes(enumType)
        ? value.filter(v => v !== enumType)
        : [...value, enumType],
    )
  }

  const selectedItems = value
    .map(v => ODD_SELECT_OPTIONS.find(o => o.enumType === v))
    .filter((o): o is SelectOption => !!o)

  const groups = ODD_TOGGLE_GROUPS.map(group => ({
    ...group,
    items: group.items
      .filter(({ type }) => type in ODD_PNG_MAP)
      .filter(({ type }) => !excludeTypes.includes(getEnumType(type))),
  })).filter(group => group.items.length > 0)

  return (
    <div css={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        css={theme => ({
          width: '100%',
          minHeight: 40,
          padding: `${theme.space.Space1} ${theme.space.Space3}`,
          border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          borderRadius: 4,
          backgroundColor: theme.semantic.ColorBackgroundAlternative,
          cursor: disabled ? 'default' : 'pointer',
          textAlign: 'left',
          display: 'flex',
          flexWrap: 'wrap',
          gap: theme.space.Space1,
          alignItems: 'center',
          position: 'relative',
          paddingRight: theme.space.Space8,
        })}
      >
        {value.length === 0 ? (
          <Text
            size="sm"
            css={theme => ({ color: theme.semantic.ColorContentReduced })}
          >
            Select
          </Text>
        ) : (
          selectedItems.map(opt => (
            <span
              key={opt.type}
              css={theme => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                backgroundColor: theme.semantic.ColorBackgroundDimmedReduced,
                borderRadius: 12,
                padding: `4px ${theme.space.Space3}`,
                fontSize: 14,
              })}
            >
              <img
                src={getOddIconSrc(opt.type, 18)}
                alt=""
                css={{
                  width: 16,
                  height: 16,
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
              />
              {opt.label}
              {!disabled && (
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation()
                    toggle(opt.enumType)
                  }}
                  css={theme => ({
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    lineHeight: 1,
                    color: theme.semantic.ColorContentReduced,
                    fontSize: 14,
                  })}
                >
                  ×
                </button>
              )}
            </span>
          ))
        )}

        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          css={theme => ({
            position: 'absolute',
            right: theme.space.Space3,
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            color: disabled
              ? theme.semantic.ColorContentDisabled
              : theme.semantic.ColorContentDefault,
          })}
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open &&
        !disabled &&
        createPortal(
          <div
            ref={dropdownRef}
            css={theme => ({
              zIndex: 10000,
              backgroundColor: theme.semantic.ColorBackgroundDefault,
              border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
              borderRadius: 4,
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              maxHeight: 260,
              overflowY: 'auto',
              overscrollBehavior: 'none',
            })}
            style={dropdownStyle}
          >
            {groups.map(group => (
              <div key={group.section}>
                <div
                  css={theme => ({
                    padding: `${theme.space.Space1} ${theme.space.Space3}`,
                    fontSize: 11,
                    color: theme.semantic.ColorContentReduced,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase' as const,
                    borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
                    backgroundColor: theme.semantic.ColorBackgroundAlternative,
                  })}
                >
                  {group.section}
                </div>
                {group.items.map(({ type, label: itemLabel }) => {
                  const enumType = getEnumType(type)
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggle(enumType)}
                      css={theme => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.space.Space2,
                        width: '100%',
                        padding: `${theme.space.Space2} ${theme.space.Space3}`,
                        backgroundColor: value.includes(enumType)
                          ? theme.semantic.ColorBackgroundDimmedReduced
                          : 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left' as const,
                        '&:hover': {
                          backgroundColor:
                            theme.semantic.ColorBackgroundAlternative,
                        },
                      })}
                    >
                      <img
                        src={getOddIconSrc(type, 22)}
                        alt={itemLabel}
                        css={{
                          width: 22,
                          height: 22,
                          objectFit: 'contain',
                          flexShrink: 0,
                        }}
                      />
                      <Text size="sm">{itemLabel}</Text>
                    </button>
                  )
                })}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}

export const OddFilterModal = ({
  type,
  label,
  metropolisId,
  currentConfig,
  onSave,
  onClose,
}: OddFilterModalProps) => {
  const [config, setConfig] = useState<OddFilterConfig>(currentConfig)
  const [loading, setLoading] = useState(false)

  const [fetchByAttributes] = useOverwatchMapOddElementsByAttributesLazyQuery()

  const handleSave = async () => {
    setLoading(true)
    try {
      const targetEnumType = getEnumType(type)
      const hasFilter =
        (config.containsEnabled && config.contains.length > 0) ||
        (config.excludesEnabled && config.excludes.length > 0)

      if (!hasFilter) {
        onSave(config, null)
        return
      }

      const result = await fetchByAttributes({
        variables: {
          metroId: metropolisId,
          targetOddElementType: targetEnumType,
          includeOddElementTypes: config.containsEnabled
            ? config.contains
            : undefined,
          excludeOddElementTypes: config.excludesEnabled
            ? config.excludes
            : undefined,
        },
      })
      const elements = result.data?.oddElementsByAttributes.elements ?? []
      onSave(config, elements)
    } finally {
      setLoading(false)
    }
  }

  const iconSrc = getOddIconSrc(type, 24)

  return createPortal(
    <div
      css={{
        position: 'fixed',
        inset: 0,
        zIndex: 10001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}
      onClick={e => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        css={theme => ({
          width: 720,
          backgroundColor: theme.semantic.ColorBackgroundDefault,
          borderRadius: theme.radius.RadiusLarge,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
        })}
      >
        <div
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: `${theme.space.Space3} ${theme.space.Space4}`,
            borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
            flexShrink: 0,
          })}
        >
          <div
            css={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.space.Space2,
            })}
          >
            <img
              src={iconSrc}
              alt=""
              css={{ width: 24, height: 24, objectFit: 'contain' }}
            />
            <Heading size="sm">{`Filter attributes for ${label}`}</Heading>
          </div>
          <IconButton
            aria-label="Close"
            size="compact"
            variant="plain"
            icon={<XIcon />}
            onClick={onClose}
          />
        </div>
        <div
          css={theme => ({
            padding: theme.space.Space4,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space.Space4,
          })}
        >
          {/* Contains */}
          <div css={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <Switch
              aria-label="Enable contains filter"
              checked={config.containsEnabled}
              onChange={v => setConfig(c => ({ ...c, containsEnabled: v }))}
              css={{ marginTop: -3 }}
            />
            <div
              css={theme => ({
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: theme.space.Space2,
              })}
            >
              <div css={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Text size="md" weight="bold">
                  Contains
                </Text>
                <InfoTooltip text="Show elements that overlap with selected ODD elements" />
              </div>
              <MultiSelect
                value={config.containsEnabled ? config.contains : []}
                onChange={v => setConfig(c => ({ ...c, contains: v }))}
                disabled={!config.containsEnabled}
                excludeTypes={config.excludes}
              />
            </div>
          </div>

          <hr
            css={theme => ({
              border: 'none',
              borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
              margin: 0,
            })}
          />

          {/* Excludes */}
          <div css={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <Switch
              aria-label="Enable excludes filter"
              checked={config.excludesEnabled}
              onChange={v => setConfig(c => ({ ...c, excludesEnabled: v }))}
              css={{ marginTop: -3 }}
            />
            <div
              css={theme => ({
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: theme.space.Space2,
              })}
            >
              <div css={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Text size="md" weight="bold">
                  Excludes
                </Text>
                <InfoTooltip text="Hide elements that overlap with selected ODD elements" />
              </div>
              <MultiSelect
                value={config.excludesEnabled ? config.excludes : []}
                onChange={v => setConfig(c => ({ ...c, excludes: v }))}
                disabled={!config.excludesEnabled}
                excludeTypes={config.contains}
              />
            </div>
          </div>
        </div>
        <div
          css={theme => ({
            display: 'flex',
            justifyContent: 'flex-end',
            gap: theme.space.Space2,
            padding: `${theme.space.Space3} ${theme.space.Space4}`,
            borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          })}
        >
          <Button label="Cancel" variant="secondary" onClick={onClose} />
          <Button
            label={loading ? 'Applying…' : 'Done'}
            variant="primary"
            disabled={loading}
            onClick={handleSave}
          />
        </div>
      </div>
    </div>,
    document.body,
  )
}
